// MenuContext — mantém o estado "open" do menu lateral vivo entre mudanças
// de rota. Antes, o Menu vivia dentro de <Left/>, que é remontado quando o
// usuário sai do Home para uma seção. Com o remount, open voltava a false
// no meio do clique no link — era isso que fazia o menu "fechar sem deixar
// clicar".
//
// Uso: envolve o <AppRoute/> em <MenuProvider/>. O Menu lê open/toggle/close
// via useMenu().

import { createContext, useContext, useState, useCallback, ReactNode } from "react";

interface Api {
  open: boolean;
  setOpen: (v: boolean) => void;
  toggle: () => void;
  close: () => void;
  openItem: string | null;
  setOpenItem: (slug: string | null) => void;
  handleOpenItem: (slug: string) => void;
}

const Ctx = createContext<Api | null>(null);

export function MenuProvider({ children }: { children: ReactNode }) {
  const [open, setOpenState] = useState(false);
  const [openItem, setOpenItem] = useState<string | null>(null);

  const setOpen = useCallback((v: boolean) => setOpenState(v), []);
  const toggle = useCallback(() => setOpenState((o) => !o), []);
  const close = useCallback(() => setOpenState(false), []);
  const handleOpenItem = useCallback((slug: string) => {
    if (!slug) return;
    setOpenItem((cur) => (cur === slug ? null : slug));
  }, []);

  return (
    <Ctx.Provider value={{ open, setOpen, toggle, close, openItem, setOpenItem, handleOpenItem }}>
      {children}
    </Ctx.Provider>
  );
}

export function useMenu(): Api {
  const api = useContext(Ctx);
  if (!api) throw new Error("useMenu fora do MenuProvider");
  return api;
}
